import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  Pagination,
  Container,
  CircularProgress,
  Button,
  Tooltip,
  IconButton,
} from "@mui/material";
import { QRCode, Switch } from "antd";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";

import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { useNavigate } from "react-router-dom";
import { useSearchEventsMutation } from "../../../../Redux/apis/ApiSlice";
import AdjustIcon from "@mui/icons-material/Adjust";
import Search from "../SearchPage/SearchPage";
import eventCardDefaultIMG from "../../../../Images/EventCardDefaultIMG.jpg";
import { motion } from "framer-motion"; // Import motion
import {
  ExpandLess,
  ExpandMore,
  Fullscreen,
  FullscreenExit,
} from "@mui/icons-material";

// Red marker icon for user location
export const userIcon = new L.Icon({
  iconUrl: "https://maps.google.com/mapfiles/ms/icons/red-dot.png",
  iconSize: [32, 32],
});

export const RecenterMap = ({ userLocation }) => {
  const map = useMap();

  useEffect(() => {
    if (userLocation) {
      map.flyTo(userLocation, 15, { duration: 1.5 }); // Smooth transition to user location
    }
  }, [map, userLocation]);

  return null;
};

const Events = ({ filters, onFilterChange }) => {
  const [page, setPage] = useState(1);
  const fgColor = "#000000";
  const [userLocation, setUserLocation] = useState([47.751076, -120.740135]);
  const [isExpanded, setIsExpanded] = useState(false); // Controls map height
  // Map Height Toggle using Switch in <Search> component
  const handleToggle = () => {
    setIsExpanded((prev) => !prev);
  };

  const navigate = useNavigate();
  const itemsPerPage = 3;

  const [searchEvents, { data: eventsData, error, isLoading }] =
    useSearchEventsMutation();

  useEffect(() => {
    setPage(1);
  }, [filters]);

  useEffect(() => {
    searchEvents({
      eventName: filters?.eventName,
      location: filters?.location,
      dateRange: filters?.dateRange,
      status: filters?.status,
      page,
      limit: itemsPerPage,
    });
  }, [filters, page]);

  useEffect(() => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setUserLocation([latitude, longitude]);
        },
        (error) => {
          console.error("Error getting location:", error);
        }
      );
    }
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handleChangePage = (event, value) => {
    setPage(value);
  };

  const handleCardClick = (event) => {
    navigate(`/detail/${event?.eventId}`, { state: { event } });
  };

  // const paginatedEvents =
  //   eventsData?.events?.slice((page - 1) * itemsPerPage, page * itemsPerPage) ||
  //   [];
  const paginatedEvents = eventsData?.events || [];

  return (
    <Box>
      <Box sx={{ height: { xs: "calc(20vh)", sm: "100%" } }}>
        <motion.div
          initial={{ height: "calc(20vh)" }}
          animate={{
            height: isExpanded ? "calc(60vh)" : "calc(20vh)",
          }}
          transition={{
            duration: 0.7,
            ease: "easeInOut",
            delay: isExpanded ? 0 : 0.3,
          }}
          style={{
            position: "relative",
            zIndex: 1000,
            overflow: "visible", // Prevents clipping
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              display: { xs: "none", sm: "block" },
              justifyContent: "space-between",
              alignItems: "center",
              zIndex: 1502,
              backgroundColor: "white",
              marginTop: "10px",
              marginRight: "10px",
              borderRadius: "4px",
              border: "2px solid rgba(0,0,0,0.2)",
            }}
          >
            <Tooltip title={isExpanded ? "Collapse Map" : "Expand Map"}>
              <IconButton onClick={handleToggle}>
                {isExpanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Tooltip>
          </Box>
          {/* Map Container */}
          <Box
            sx={{
              flexGrow: 1,
              height: { xs: "5%", sm: "100%" },
              overflow: "hidden", // Keep hidden only for map
              // display: { xs: "none", sm: "block" },
              visibility: { xs: "hidden", sm: "visible", md: "visible" },
            }}
          >
            <MapContainer
              center={userLocation}
              zoom={userLocation ? 20 : 2}
              style={{
                height: "100%",
                width: "100%",
              }}
            >
              {userLocation && <RecenterMap userLocation={userLocation} />}
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />
              {userLocation && (
                <>
                  <RecenterMap userLocation={userLocation} />
                  <Marker position={userLocation} icon={userIcon}>
                    <Popup>
                      <Typography variant="h6">Your Location</Typography>
                    </Popup>
                  </Marker>
                </>
              )}
              {eventsData?.events?.map((event) => (
                <Marker
                  key={event.eventId}
                  position={[event.latitude, event.longitude]}
                >
                  <Popup>
                    <Typography variant="h6">{event.eventName}</Typography>
                    <Typography variant="body2">{event.location}</Typography>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </Box>

          {/* Search Container */}
          <Box
            sx={{
              position: { xs: "static", sm: "absolute", md: "absolute" },
              top: {
                sm: isExpanded ? "calc(100% - 10px)" : "calc(100% + 80px)",
                md: isExpanded ? "calc(100% - 10px)" : "calc(100% - 50px)",
              }, // Adjusted to sit at the bottom
              left: "50%",
              transform: {
                xs: "translateY(0%)",
                sm: "translate(-50%, -50%)",
                md: "translate(-50%, -50%)",
              }, // Center both horizontally & adjust vertically
              zIndex: 1501,
              width: "100%",
              overflow: "visible",
              transition: "top 0.7s ease-in-out", // Smooth movement
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 40 }} // Starts lower
              animate={{ opacity: 1, y: isExpanded ? 0 : 40 }} // Moves up when expanded
              exit={{ opacity: 0, y: 40 }}
              transition={{
                duration: 0.5,
                ease: "easeInOut",
                delay: isExpanded ? 0.1 : 0.3,
              }}
              style={{
                width: "100%",
              }}
            >
              <Container maxWidth="lg">
                <Search
                  filters={filters}
                  onFilterChange={onFilterChange}
                  onToggle={handleToggle}
                />
              </Container>
            </motion.div>
          </Box>
        </motion.div>
      </Box>
      <Box
        sx={{
          mt: isExpanded
            ? { xs: 39, sm: 30, md: 20 }
            : { xs: 43, sm: 40, md: 20 },
        }}
      >
        <Container maxWidth="lg" sx={{ justifyContent: "center" }}>
          <Typography align="center" sx={styles.title}>
            Find Events That <span className="count-shine">Excite You</span>
          </Typography>
          <Typography
            align="center"
            variant="body1"
            color="text.secondary"
            sx={{ mt: 2, textAlign: { xs: "center", md: "center" } }}
          >
            Uncover hidden gems, trending happenings, and unforgettable
            experiences - curated to ignite your passion and fuel your
            curiosity.
          </Typography>

          <Grid container spacing={3} sx={{ mt: 8, justifyContent: "center" }}>
            {isLoading ? (
              <Grid
                item
                xs={12}
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <GradientCircularProgress />
              </Grid>
            ) : paginatedEvents.length > 0 ? (
              paginatedEvents.map((event, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Card
                    sx={{
                      borderRadius: "8px",
                      height: "100%",
                      padding: "16px",
                      cursor: "pointer",
                      boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
                      transition: "box-shadow 0.3s ease",
                      "&:hover": {
                        boxShadow: "0px 8px 30px rgba(0, 0, 0, 0.2)",
                      },
                    }}
                    onClick={() => handleCardClick(event)}
                  >
                    {event.eventPhoto?.length > 0 ? (
                      <CardMedia
                        component="img"
                        height="200"
                        image={event.eventPhoto[0]}
                        alt={event.eventName}
                        sx={{ borderRadius: "8px" }}
                      />
                    ) : (
                      <Box
                        sx={{
                          height: 200,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "#f9f9f9",
                          borderRadius: "8px",
                        }}
                      >
                        <QRCode
                          value={`https://gxtickets.com/detail/${event.eventId}`}
                          size={150}
                          level="H"
                          fgColor={fgColor}
                          bgColor="#ffffff"
                          includeMargin
                        />
                      </Box>
                    )}
                    <CardContent>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 400,
                            fontSize: "14px",
                            color: "#535353",
                            mt: 1,
                          }}
                        >
                          {new Date(event.date).toLocaleDateString("en-US", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                          })}
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: 400,
                            fontSize: "14px",
                            color: "#535353",
                            mt: 1,
                          }}
                        >
                          {event?.ticket?.[0]?.subTickets?.every(
                            (sub) => sub.quantity === 0
                          )
                            ? "SOLD OUT"
                            : event?.status?.toUpperCase()}
                        </Typography>
                      </Box>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: "20px",
                          color: "#000",
                          mt: 1,
                        }}
                      >
                        {event?.eventName}
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 400,
                          fontSize: "14px",
                          color: "#535353",
                          mt: 1,
                        }}
                      >
                        Location:{" "}
                        {event.location.length > 45
                          ? `${event.location.slice(0, 45)}...`
                          : event.location}
                      </Typography>
                      <Typography
                        sx={{
                          mt: 1,
                          fontWeight: 400,
                          fontSize: "12px",
                          color: "#535353",
                        }}
                      >
                        {event.description.length > 100
                          ? `${event.description.slice(0, 100)}...`
                          : event.description}
                      </Typography>
                      {/* <Button variant="text" sx={{ mt: 2, color: "#535353", borderRadius: 2, "&:hover": { border: "1px solid #535353" } }}>
                        Book Now
                      </Button> */}
                    </CardContent>
                  </Card>
                </Grid>
              ))
            ) : (
              <Grid item xs={12}>
                <Typography variant="h6" align="center">
                  No events available
                </Typography>
              </Grid>
            )}
          </Grid>
        </Container>
        <Box sx={{ display: "flex", justifyContent: "center", marginTop: 4 }}>
          <Pagination
            // count={Math.ceil((eventsData?.events?.length || 0) / itemsPerPage)}
            count={eventsData?.totalPages || 1}
            page={page}
            onChange={handleChangePage}
          />
        </Box>
      </Box>
    </Box>
  );
};

function GradientCircularProgress() {
  return (
    <>
      <svg width={0} height={0}>
        <defs>
          <linearGradient id="my_gradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#e01cd5" />
            <stop offset="100%" stopColor="#1CB5E0" />
          </linearGradient>
        </defs>
      </svg>
      <CircularProgress
        size={80}
        sx={{ "svg circle": { stroke: "url(#my_gradient)" } }}
      />
    </>
  );
}

export default Events;

const styles = {
  title: { fontWeight: 700, fontSize: { xs: 24, sm: 32, md: 40 } },
};
